import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTrackCreditWarningPopoverClicked, useTrackCreditWarningPopoverDismissed } from '@air/analytics';
import { Popover } from '@air/primitive-popover';
import { useAirModal } from '@air/provider-modal';

import { CompactCreditUsageMeter } from '~/components/CreditUsageMeter/CompactCreditUsageMeter';
import { CreditUsageUpsellAlert } from '~/components/CreditUsageUpsellAlert';
import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { useWorkspaceSidebarStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarStore';
import { ONE_SECOND } from '~/constants/unitsOfTime';
import { useDismissedCreditUsageAlert } from '~/hooks/creditUsageAlerts/useDismissedCreditUsageAlert';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCreditStats } from '~/swr-hooks/workspaces/useCreditStats';
import { getCreditUsageUpsellAlertCandidate } from '~/utils/creditUsageAlerts/getCreditUsageUpsellAlertCandidate';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';

export const WorkspaceSidebarFooterCreditUsageMeter = memo(() => {
  const { data: subscription } = useSubscriptionContext();
  const { data: creditStats } = useCreditStats();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canEditPlan = canManageSubscription(permissions);
  const [showPlansModal] = useAirModal(PlansModal);
  const [showDemoFormModal] = useAirModal(DemoFormModal);
  const { trackCreditWarningPopoverClicked } = useTrackCreditWarningPopoverClicked();
  const { trackCreditWarningPopoverDismissed } = useTrackCreditWarningPopoverDismissed();
  const creditAnalyticsParams = useCreditAnalyticsParams();

  const alertCandidate = useMemo(() => {
    if (!creditStats || !subscription || !permissions) {
      return null;
    }

    if (!creditStats.allocated) {
      return null;
    }

    return getCreditUsageUpsellAlertCandidate({
      creditStats,
      subscription,
      canEditPlan,
      showPlansModal: () => showPlansModal({ leadFormTrigger: 'credit_popover' }),
      showDemoFormModal: () => showDemoFormModal({ trigger: 'credit_popover' }),
    });
  }, [canEditPlan, creditStats, subscription, showPlansModal, permissions, showDemoFormModal]);

  const { isDismissed, onDismiss } = useDismissedCreditUsageAlert({
    alertType: 'meterUpsell',
    creditUsageState: alertCandidate?.creditUsageState,
    nextCycleStartAt: creditStats?.nextCycleStartAt,
    workspaceId: subscription?.workspaceId,
    persistenceType: alertCandidate?.useSessionStorage ? 'sessionStorage' : 'localStorage',
  });

  // CreditUsageUpsellAlert calls onDismiss after CTA click; ref prevents double-tracking
  const ctaClickedRef = useRef(false);
  const isDepleted = alertCandidate?.creditUsageState === 'depleted';

  const handleDismiss = useCallback(() => {
    // Only track dismiss for warning states (not depleted) since there's no depleted_dismissed event
    if (!isDepleted && creditAnalyticsParams && !ctaClickedRef.current) {
      trackCreditWarningPopoverDismissed({
        type: 'popover',
        ...creditAnalyticsParams,
      });
    }
    ctaClickedRef.current = false;
    onDismiss();
  }, [creditAnalyticsParams, isDepleted, onDismiss, trackCreditWarningPopoverDismissed]);

  const handleCtaClick = useCallback(() => {
    ctaClickedRef.current = true;
    if (creditAnalyticsParams) {
      trackCreditWarningPopoverClicked({
        type: 'popover',
        ...creditAnalyticsParams,
      });
    }
    alertCandidate?.ctaButton?.onClick();
  }, [alertCandidate?.ctaButton, creditAnalyticsParams, trackCreditWarningPopoverClicked]);

  const isSidebarOpen = useWorkspaceSidebarStore((state) => state.open);

  const shouldShowAlert = !!alertCandidate && !isDismissed && isSidebarOpen;

  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    if (!shouldShowAlert) {
      setShowAlert(false);
      return;
    }

    const timeout = setTimeout(() => {
      setShowAlert(true);
    }, ONE_SECOND);

    return () => {
      clearTimeout(timeout);
    };
  }, [shouldShowAlert]);

  return (
    <Popover
      onOpenAutoFocus={(e) => e.preventDefault()}
      open={showAlert}
      alignOffset={8}
      className="rounded-8 p-0"
      trigger={
        <div>
          <CompactCreditUsageMeter includeMeterBar disableHoverCard={showAlert} />
        </div>
      }
    >
      {!!alertCandidate && showAlert && (
        <CreditUsageUpsellAlert
          title={alertCandidate.title}
          description={alertCandidate.description}
          ctaButton={alertCandidate.ctaButton ? { ...alertCandidate.ctaButton, onClick: handleCtaClick } : undefined}
          dismissButton={alertCandidate.dismissButton}
          onDismiss={handleDismiss}
        />
      )}
    </Popover>
  );
});

WorkspaceSidebarFooterCreditUsageMeter.displayName = 'WorkspaceSidebarFooterCreditUsageMeter';
